'use client'

import React, { useState, useMemo, useCallback, useRef } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { debounce, uniqBy } from 'lodash'

import { RootState } from '@/lib/redux/store'
import { getCompanyContacts } from '@/api/company/contacts'
import { cn, formatPhoneNumber, getWordInitials } from '@/lib/utils'
import useInfiniteScroll from '@/lib/hooks/useInfiniteScroll'
import Avatar from '@/components/livecontact/Avatar'
import EmptyState from '@/components/livecontact/EmptyState'
import Skeleton from '@/components/livecontact/Skeleton'
import Spinner from '@/components/livecontact/Spinner'
import LcIcon from '@/components/livecontact/icons'
import { SearchInput } from '@/components/livecontact/Input'

type ContactSelectorProps = {
  value: number[]
  onChange: (contactIds: number[]) => void
}

/** 16px checkbox visual — the real input is visually hidden inside the row label. */
const CheckSquare = ({ checked }: { checked: boolean }) => (
  <span
    aria-hidden
    className={cn(
      'inline-flex size-4 shrink-0 items-center justify-center rounded-[4px] transition-[background-color,box-shadow,transform] duration-[160ms] ease-out-strong group-active:scale-[0.92]',
      checked
        ? 'bg-brand text-brand-ink shadow-[0_0_0_1px_rgb(var(--lc-brand))]'
        : 'bg-surface shadow-[0_0_0_1px_rgb(var(--lc-line-2))] group-hover:shadow-[0_0_0_1px_rgb(var(--lc-brand)/0.6)]'
    )}
  >
    <LcIcon
      name="check"
      size={11}
      strokeWidth={2.5}
      className={cn(
        'transition-[opacity,transform] duration-[160ms] ease-out-strong',
        checked ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
      )}
    />
  </span>
)

const ContactRowSkeleton = () => (
  <div
    className="flex items-center gap-3 border-b border-line px-3 py-2.5 last:border-b-0"
    aria-hidden
  >
    <Skeleton className="size-4 rounded-[4px]" />
    <Skeleton className="size-8 rounded-full" />
    <div className="flex flex-1 flex-col gap-1.5">
      <Skeleton className="h-3.5 w-2/5" />
      <Skeleton className="h-3 w-1/4" />
    </div>
  </div>
)

/** Searchable, infinitely-scrolling contact checklist used when building groups/audiences. */
const ContactSelector = ({ value, onChange }: ContactSelectorProps) => {
  const companyId = useSelector(({ company }: RootState) => company.data?.id)
  const [contactSearchValue, setContactSearchValue] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const pageSize = 10

  const handleSearch = useMemo(
    () =>
      debounce((next: string) => {
        setContactSearchValue(next)
        if (containerRef.current) containerRef.current.scrollTop = 0
      }, 500),
    []
  )

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['currentCompany', companyId, 'contacts', 'selector', contactSearchValue],
    queryFn: ({ pageParam }) =>
      getCompanyContacts({
        page: pageParam,
        size: pageSize,
        search: contactSearchValue,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.lastPage) return lastPage.page + 1
      return undefined
    },
    enabled: !!companyId,
  })

  const contacts = useMemo(() => {
    if (!data?.pages) return []
    return uniqBy(
      data.pages.flatMap((page) => page.data),
      'id'
    )
  }, [data?.pages])

  useInfiniteScroll({
    containerRef,
    hasMore: hasNextPage && !isFetchingNextPage,
    onLoadMore: fetchNextPage,
    threshold: 10,
  })

  const toggleContactSelection = useCallback(
    (contactId: number) => {
      if (value.includes(contactId)) onChange(value.filter((id) => id !== contactId))
      else onChange([...value, contactId])
    },
    [value, onChange]
  )

  const handleSelectAllOnPage = useCallback(() => {
    const allLoadedContactIds = contacts.map((c) => c.id)
    const allCurrentSelected = allLoadedContactIds.every((id) => value.includes(id))

    if (allCurrentSelected) {
      onChange(value.filter((id) => !allLoadedContactIds.includes(id)))
    } else {
      const newIds = [...value]
      allLoadedContactIds.forEach((id) => {
        if (!newIds.includes(id)) newIds.push(id)
      })
      onChange(newIds)
    }
  }, [contacts, value, onChange])

  const handleClearAll = useCallback(() => onChange([]), [onChange])

  const allLoadedSelected = useMemo(
    () => contacts.length > 0 && contacts.every((contact) => value.includes(contact.id)),
    [contacts, value]
  )

  return (
    <div className="flex flex-col gap-3">
      <SearchInput
        defaultValue={contactSearchValue}
        onChange={(e) => handleSearch(e.target.value)}
        placeholder="Search contacts"
        aria-label="Search contacts"
      />

      <div className="flex items-center justify-between text-[12px] tracking-[-0.2px]">
        <label className="group inline-flex cursor-pointer select-none items-center gap-2 text-ink-3">
          <input
            type="checkbox"
            checked={allLoadedSelected}
            onChange={handleSelectAllOnPage}
            disabled={contacts.length === 0}
            className="peer sr-only"
          />
          <CheckSquare checked={allLoadedSelected} />
          <span className="peer-focus-visible:underline">
            Select all loaded ({contacts.length})
          </span>
        </label>
        {value.length > 0 && (
          <button
            type="button"
            onClick={handleClearAll}
            className="rounded-[6px] font-medium text-ink-3 outline-none transition-colors duration-[120ms] hover:text-ink focus-visible:ring-2 focus-visible:ring-brand/40"
          >
            Clear all ({value.length})
          </button>
        )}
      </div>

      <div className="overflow-hidden rounded-[12px] bg-surface shadow-input">
        <div
          ref={containerRef}
          className="max-h-96 overflow-y-auto lc-scrollbar-thin sm:max-h-64"
          aria-busy={isLoading || undefined}
        >
          {isLoading ? (
            [0, 1, 2, 3, 4].map((i) => <ContactRowSkeleton key={i} />)
          ) : contacts.length === 0 ? (
            <EmptyState
              size="sm"
              className="px-6 py-10"
              icon={<LcIcon name={contactSearchValue ? 'search' : 'user-round'} size={24} />}
              title={contactSearchValue ? 'No contacts found' : 'No contacts yet'}
              description={
                contactSearchValue
                  ? 'Try a different name or number.'
                  : 'Add contacts first, then pick them here.'
              }
            />
          ) : (
            <>
              {contacts.map((contact) => {
                const fullName =
                  `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim() || 'No name'
                const checked = value.includes(contact.id)
                return (
                  <label
                    key={contact.id}
                    className={cn(
                      'group flex cursor-pointer select-none items-center gap-3 border-b border-line px-3 py-2.5 transition-colors duration-[120ms] last:border-b-0 hover:bg-canvas/70 dark:hover:bg-surface-2',
                      checked && 'bg-brand/[0.04]'
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleContactSelection(contact.id)}
                      aria-label={`Select ${fullName}`}
                      className="peer sr-only"
                    />
                    <CheckSquare checked={checked} />
                    <Avatar
                      src={contact.avatar}
                      name={fullName}
                      initials={getWordInitials(fullName)}
                      size={32}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[14px] font-medium leading-[1.2] tracking-[-0.2px] text-ink">
                        {fullName}
                      </div>
                      <div className="mt-0.5 truncate text-[12px] leading-[1.2] text-ink-4">
                        {formatPhoneNumber(contact.phone)}
                      </div>
                    </div>
                    {contact.vCards && contact.vCards.length > 0 && (
                      <span className="inline-flex h-5 shrink-0 items-center rounded-full bg-violet/10 px-2 text-[11px] font-medium leading-none text-violet">
                        {contact.vCards.length} card{contact.vCards.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </label>
                )
              })}

              {isFetchingNextPage && (
                <div className="flex items-center justify-center gap-2 border-t border-line py-3 text-[12px] text-ink-4">
                  <Spinner className="size-3.5" />
                  Loading more…
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between rounded-[10px] bg-canvas px-3 py-2.5 text-[13px] tracking-[-0.2px] dark:bg-surface-2">
        <span className="font-medium text-ink">Selected contacts</span>
        <span className="text-ink-3 tabular-nums">
          {value.length === 0 ? 'None yet' : `${value.length} will be added`}
        </span>
      </div>
    </div>
  )
}

export default ContactSelector
